import React, { useEffect, useState } from "react";
import axiosInstance from "../../../../axios";
import Modal from "../../../Other/Modal";
import { 
  Plus, Search, Edit3, Trash2, Layers, 
  ArrowUpDown, FolderOpen, AlertCircle
} from "lucide-react";
import { Loading } from "@/pages/Other/Loading";
import { categoryActions } from "@/_actions/category.actions";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { t } from "i18next";

const { REACT_APP_LOGIN_API_BASEURL } = process.env;

export default function AllCategories() {
  const dispatch = useDispatch();
  const { categories, isLoading, error } = useSelector(state => state?.category);

  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [categoryData, setCategoryData] = useState({
    id: "",
    categoryName: "",
    categoryDescription: "",
    status: 1,
  });
  const [deleteCategoryId, setDeleteCategoryId] = useState(null);

  // Add search term and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const staticData = [
    {
      name: "Total Category",
      total: categories.length || "0",
      boxShadow: "Dashboard-color-one",
      color: "color-one",
    },
  ];

  useEffect(() => {
    dispatch(categoryActions.getCategories());
  }, [dispatch]);

  const handleShowModal = (category = null) => {
    if (category) {
      setIsEditing(true);
      setCategoryData(category);
    } else {
      setIsEditing(false);
      setCategoryData({
        id: "",
        categoryName: "",
        categoryDescription: "",
        status: 1,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => setShowModal(false);

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await axiosInstance.post(
          `${REACT_APP_LOGIN_API_BASEURL}/category/${categoryData.id}/edit`,
          categoryData
        );
      } else {
        await axiosInstance.post(
          `${REACT_APP_LOGIN_API_BASEURL}/category`,
          categoryData
        );
      }
      dispatch(categoryActions.getCategories());
      handleCloseModal();
    } catch (err) {
      console.error("Error saving category:", err);
    }
  };

  const handleDeleteCategory = async (id) => {
    setDeleteCategoryId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await axiosInstance.delete(
        `${REACT_APP_LOGIN_API_BASEURL}/category/${deleteCategoryId}`
      );
      console.log("Delete response:", response);
      dispatch(categoryActions.getCategories())
      if (response?.data?.errorCode === 500) {
        toast.warning(response?.data?.message || t("Category deleted successfully"));
      }
      else {
        toast.success(response?.data?.message || t("Category deleted successfully"));
      }
      setShowDeleteModal(false);
    } catch (err) {
      toast.error("Error deleting category");
      setShowDeleteModal(false);
      console.error("Error deleting category:", err);

    }
  };

  // Sorting handler
  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort categories
  const filteredCategories = (categories || []).filter((category) => {
    const matchesSearch = category.categoryName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && Number(category.status) === 1) ||
      (statusFilter === "inactive" && Number(category.status) === 0);
    return matchesSearch && matchesStatus;
  });

  const sortedCategories = React.useMemo(() => {
    if (!sortConfig.key) return filteredCategories;
    return [...filteredCategories].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // For status, compare as number
      if (sortConfig.key === "status") {
        aValue = Number(aValue);
        bValue = Number(bValue);
        return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      // For date, compare as date (assuming createdAt exists)
      if (sortConfig.key === "createdAt" || sortConfig.key === "date") {
        return sortConfig.direction === "asc"
          ? new Date(aValue) - new Date(bValue)
          : new Date(bValue) - new Date(aValue);
      }

      // Default: compare as string
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortConfig.direction === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      return 0;
    });
  }, [filteredCategories, sortConfig]);

  const categoryToDelete = (categories || []).find(c => c.id === deleteCategoryId);

  return (
    <div className="w-full bg-white min-h-screen px-4 py-6 md:px-8 md:py-8 rounded-2xl border border-neutral-200/80 shadow-sm max-w-7xl mx-auto">
      {/* Header section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-5 mb-6 border-b border-neutral-100 gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl md:text-3xl font-black text-neutral-900 tracking-tight">
              Course Categories
            </h1>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs md:text-sm font-extrabold bg-neutral-100 text-neutral-800 border border-neutral-200">
              {(categories || []).length} Total
            </span>
          </div>
          <p className="text-neutral-600 text-sm md:text-base font-semibold mt-1">
            Manage and structure classification tracks for learning content.
          </p>
        </div>
      </div>

      {/* Control Panel (Search + Filter + Add) */}
      <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-4 mb-6">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1">
          {/* Search Input Box */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-neutral-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search categories..."
              className="w-full pl-10 pr-4 py-3 bg-white border border-neutral-200 rounded-xl text-base font-bold text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 transition-all shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Status Filter Dropdown */}
          <div className="relative min-w-[170px]">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-4 pr-10 py-3 bg-white border border-neutral-200 rounded-xl text-base font-extrabold text-neutral-900 focus:outline-none focus:border-neutral-900 transition-all appearance-none cursor-pointer shadow-sm"
              style={{
                backgroundImage: `url("data:image/svg+xml;utf8,<svg fill='none' height='24' stroke='%23111827' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><polyline points='6 9 12 15 18 9'></polyline></svg>")`,
                backgroundPosition: 'right 14px center',
                backgroundRepeat: 'no-repeat',
                backgroundSize: '16px'
              }}
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>

        {/* Add Category button */}
        <button
          onClick={() => handleShowModal()}
          className="flex items-center justify-center gap-2 px-6 py-3 bg-black hover:bg-neutral-800 text-white rounded-xl font-extrabold text-base shadow-sm transition-all active:scale-95 whitespace-nowrap"
        >
          <Plus size={18} />
          Add Category
        </button>
      </div>

      {/* Table section */}
      {isLoading ? (
        <div className="flex justify-center py-12 bg-white border border-neutral-200 rounded-xl">
          <Loading />
        </div>
      ) : error ? (
        <div className="bg-neutral-50 border border-neutral-200 text-neutral-800 p-6 rounded-xl text-center font-bold text-base">
          {error}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-neutral-50 border-b border-neutral-200 text-sm font-black text-neutral-800 uppercase tracking-wider select-none">
                  <th className="w-[10%] py-4 px-5 text-center font-black">ID</th>
                  <th
                    className="w-[30%] py-4 px-5 font-black cursor-pointer select-none"
                    onClick={() => handleSort("categoryName")}
                  >
                    <div className="flex items-center gap-1.5">
                      Category Name
                      {sortConfig.key === "categoryName" ? (
                        <span className="text-neutral-900 text-sm">
                          {sortConfig.direction === "asc" ? "▲" : "▼"}
                        </span>
                      ) : (
                        <ArrowUpDown className="text-neutral-500 w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th className="w-[35%] py-4 px-5 font-black">Description</th>
                  <th
                    className="w-[15%] py-4 px-5 font-black cursor-pointer select-none"
                    onClick={() => handleSort("createdOn")}
                  >
                    <div className="flex items-center gap-1.5">
                      Date
                      {sortConfig.key === "createdOn" ? (
                        <span className="text-neutral-900 text-sm">
                          {sortConfig.direction === "asc" ? "▲" : "▼"}
                        </span>
                      ) : (
                        <ArrowUpDown className="text-neutral-500 w-4 h-4" />
                      )}
                    </div>
                  </th>
                  <th className="w-[10%] py-4 px-5 font-black text-center border-l border-neutral-100">Status</th>
                  <th className="w-[10%] py-4 px-5 font-black text-center border-l border-neutral-100">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {sortedCategories.length === 0 ? (
                  <tr>
                    <td className="py-12 text-center text-base font-bold text-neutral-400" colSpan={6}>
                      No records found
                    </td>
                  </tr>
                ) : (
                  sortedCategories.map((category) => (
                    <tr key={category.id} className="hover:bg-neutral-50/30 transition-colors">
                      <td className="py-5 px-5 text-center text-base font-bold text-neutral-500">{category.id}</td>
                      <td className="py-5 px-5 text-base font-extrabold text-neutral-900">{category.categoryName}</td>
                      <td className="py-5 px-5 text-base font-semibold text-neutral-700">{category.categoryDescription}</td>
                      <td className="py-5 px-5 text-base font-bold text-neutral-700 whitespace-nowrap">{category.createdOn}</td>
                      <td className="py-5 px-5 text-center border-l border-neutral-100">
                        {Number(category.status) === 1 ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-black uppercase tracking-wider rounded bg-neutral-900 text-white whitespace-nowrap">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-3 py-1 text-xs font-black uppercase tracking-wider rounded border border-neutral-200 bg-white text-neutral-400 whitespace-nowrap">
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="py-5 px-5 text-center border-l border-neutral-100">
                        <div className="flex items-center justify-center gap-3">
                          <button
                            onClick={() => handleShowModal(category)}
                            className="p-2 bg-white hover:bg-black text-neutral-500 hover:text-white rounded-lg border border-neutral-200 transition-all hover:scale-105 active:scale-95"
                            title="Edit"
                          >
                            <Edit3 size={16} />
                          </button>
                          <button
                            onClick={() => handleDeleteCategory(category.id)}
                            className="p-2 bg-white hover:bg-neutral-800 text-neutral-500 hover:text-white rounded-lg border border-neutral-200 transition-all hover:scale-105 active:scale-95"
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add / Edit Category Modal */}
      {showModal && (
        <Modal isOpen={showModal} onClose={handleCloseModal} width="520px">
          <div className="p-2 z-99 text-left">
            <div className="bg-neutral-50 border-b border-neutral-100 rounded-t-2xl p-6 -mx-6 -mt-5 mb-6 flex items-center gap-2.5">
              <FolderOpen className="text-neutral-700 w-6 h-6" />
              <span className="font-black text-neutral-900 text-xl">
                {isEditing ? "Edit Category" : "Add Category"}
              </span>
            </div>
            <form onSubmit={handleSaveCategory} className="space-y-5">
              <div>
                <label className="block font-black text-neutral-700 text-sm uppercase tracking-wider mb-2">Category Name</label>
                <input
                  type="text"
                  className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-base font-bold text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 transition-all shadow-sm"
                  placeholder="e.g. Computer Science"
                  value={categoryData.categoryName}
                  onChange={(e) =>
                    setCategoryData({
                      ...categoryData,
                      categoryName: e.target.value,
                    })
                  }
                  required
                />
              </div>
              <div>
                <label className="block font-black text-neutral-700 text-sm uppercase tracking-wider mb-2">Description</label>
                <textarea
                  className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-base font-bold text-neutral-800 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 transition-all shadow-sm"
                  placeholder="Enter classification details..."
                  rows={4}
                  value={categoryData.categoryDescription}
                  onChange={(e) =>
                    setCategoryData({
                      ...categoryData,
                      categoryDescription: e.target.value,
                    })
                  }
                />
              </div>
              <div>
                <label className="block font-black text-neutral-700 text-sm uppercase tracking-wider mb-2">Status</label>
                <select
                  className="w-full px-4 py-3 bg-white border border-neutral-200 rounded-xl text-base font-extrabold text-neutral-800 focus:outline-none focus:border-neutral-900 transition-all shadow-sm appearance-none cursor-pointer"
                  value={categoryData.status}
                  onChange={(e) =>
                    setCategoryData({ ...categoryData, status: Number(e.target.value) })
                  }
                  style={{
                    backgroundImage: `url("data:image/svg+xml;utf8,<svg fill='none' height='24' stroke='%23374151' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' viewBox='0 0 24 24' width='24' xmlns='http://www.w3.org/2000/svg'><polyline points='6 9 12 15 18 9'></polyline></svg>")`,
                    backgroundPosition: 'right 14px center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '16px'
                  }}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
              <div className="flex justify-end space-x-3 pt-5 border-t border-neutral-100">
                <button
                  type="button"
                  className="px-6 py-3 bg-neutral-100 hover:bg-neutral-200 text-neutral-800 rounded-xl font-extrabold text-base transition-all active:scale-95 border border-transparent"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-black hover:bg-neutral-800 text-white rounded-xl font-extrabold text-base shadow-sm transition-all active:scale-95"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          width="460px"
        >
          <div className="p-2 text-left">
            <div className="flex items-center gap-2.5 text-neutral-900 mb-4">
              <AlertCircle className="w-7 h-7" />
              <h2 className="text-2xl font-black tracking-tight">Confirm Delete</h2>
            </div>
            <p className="text-neutral-600 text-base font-semibold leading-relaxed">
              Are you sure you want to delete the category{" "}
              <span className="text-neutral-900 font-extrabold">
                "{categoryToDelete?.categoryName || "this category"}"
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3 mt-6 pt-5 border-t border-neutral-100">
              <button
                type="button"
                className="px-6 py-3 bg-neutral-100 hover:bg-neutral-200 text-neutral-800 rounded-xl font-extrabold text-base transition-all active:scale-95 border border-transparent"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-6 py-3 bg-black hover:bg-neutral-800 text-white rounded-xl font-extrabold text-base shadow-sm transition-all active:scale-95"
                onClick={handleConfirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
